'use client';

import { createRevealObserver } from '@/lib/reveal-observer';
import React, { useEffect, useRef } from 'react';
import Link from 'next/link';

export default function FinalCTASection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = createRevealObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.scroll-fade').forEach((el) => {
              el.classList.add('visible');
            });
          }
        });
      },
      { rootMargin: '0px 0px -40px 0px', threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-32 px-6 border-t border-primary/10 relative overflow-hidden"
    >
      {/* Background atmosphere */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div
          className="absolute blob-primary animate-pulse-glow"
          style={{
            width: '60vw',
            height: '60vh',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </div>

      <div className="max-w-4xl mx-auto text-center relative z-10">
        <p className="section-label scroll-fade mb-8">Begin Your Journey</p>

        <h2 className="text-display font-sans font-semibold text-foreground scroll-fade scroll-fade-delay-1 mb-6">
          The right capital can change the{' '}
          <span className="font-serif italic text-primary">trajectory of a company.</span>
        </h2>

        <p className="text-base text-muted-foreground font-light leading-relaxed scroll-fade scroll-fade-delay-2 mb-4 max-w-2xl mx-auto">
          But finding it requires more than a pitch.
        </p>
        <p className="text-base text-muted-foreground font-light leading-relaxed scroll-fade scroll-fade-delay-2 mb-4 max-w-2xl mx-auto">
          It requires the right strategy, the right positioning and the right relationships.
        </p>
        <p className="text-base text-foreground font-medium leading-relaxed scroll-fade scroll-fade-delay-3 mb-12 max-w-xl mx-auto">
          Aregn Capital helps you find them.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center scroll-fade scroll-fade-delay-3">
          <Link
            href="/contact"
            className="px-10 py-4 rounded-full bg-primary text-primary-foreground text-sm font-semibold tracking-widest uppercase hover:bg-accent transition-all duration-300 hover:scale-105 active:scale-100"
          >
            Raise Capital
          </Link>
          <Link
            href="/contact"
            className="px-10 py-4 rounded-full border border-primary/30 text-foreground text-sm font-semibold tracking-widest uppercase hover:bg-primary/10 hover:border-primary/50 transition-all duration-300"
          >
            Join Our Investor Network
          </Link>
        </div>
      </div>
    </section>
  );
}
